import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Grid, Card, CardHeader, CardContent, CardMedia, Typography, List, ListItem, ListItemText, Divider, CircularProgress, TextField, Button, Stack } from '@mui/material';
import { AuthContext } from '../components/Auth';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../components/AppProvider';

const RecipesPage = () => {
  const { firestoreInstance } = useContext(AuthContext);
  const { config } = useContext(AppContext);
  const pathnames = config.pathnames;
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize] = useState(12);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!firestoreInstance) {
      setRecipes([]);
      setLoading(false);
      return () => {};
    }

    const matchesIngredient = (r, term) => {
      if (!term) return true;
      const t = term.toLowerCase();
      const ings = r.ingredients || [];
      return ings.some((ing) => {
        if (!ing) return false;
        if (typeof ing === 'string') return ing.toLowerCase().includes(t);
        if (typeof ing === 'object') {
          if (ing.name && typeof ing.name === 'string' && ing.name.toLowerCase().includes(t)) return true;
          return JSON.stringify(ing).toLowerCase().includes(t);
        }
        return false;
      });
    };

    const fetchInitial = async () => {
      setLoading(true);
      setRecipes([]);
      setLastDoc(null);
      setHasMore(true);
      try {
        const firestore = await import('firebase/firestore');
        const { collection, query, orderBy, getDocs, limit } = firestore;
        const colRef = collection(firestoreInstance, 'sharedRecipes');
        // if searching, fetch all and filter client-side (firestore array-contains on object fields is unreliable)
        if (searchTerm) {
          const q = query(colRef, orderBy('sharedAt', 'desc'));
          const snap = await getDocs(q);
          if (cancelled) return;
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const filtered = items.filter((r) => matchesIngredient(r, searchTerm));
          setRecipes(filtered);
          setHasMore(false);
          setLastDoc(null);
        } else {
          const q = query(colRef, orderBy('sharedAt', 'desc'), limit(pageSize));
          const snap = await getDocs(q);
          if (cancelled) return;
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setRecipes(items);
          const last = snap.docs[snap.docs.length - 1] || null;
          setLastDoc(last);
          setHasMore((snap.docs.length ?? 0) >= pageSize);
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || String(err));
        setLoading(false);
      }
    };

    fetchInitial();

    return () => {
      cancelled = true;
    };
  }, [firestoreInstance, pageSize, searchTerm]);

  const loadMore = async () => {
    if (!firestoreInstance || !lastDoc || !hasMore) return;
    setLoadingMore(true);
    try {
      const firestore = await import('firebase/firestore');
      const { collection, query, orderBy, getDocs, limit, startAfter } = firestore;
      const colRef = collection(firestoreInstance, 'sharedRecipes');
      const q = query(colRef, orderBy('sharedAt', 'desc'), startAfter(lastDoc), limit(pageSize));
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecipes((prev) => [...prev, ...items]);
      const last = snap.docs[snap.docs.length - 1] || null;
      setLastDoc(last);
      setHasMore((snap.docs.length ?? 0) >= pageSize);
    } catch (err) {
      setError(err.message || String(err));
    }
    setLoadingMore(false);
  };

  return (
    <Box sx={{ p: 2, flexGrow: 1 }}>
      <Helmet>
        <title>Recipes — MonadX</title>
        <meta name="description" content="Browse recipes shared on MonadX. Search by ingredient and view recipe details." />
        <meta property="og:title" content="Recipes — MonadX" />
      </Helmet>
      <Typography variant="h5" gutterBottom>Recipes</Typography>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Box sx={{ my: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'nowrap' }} alignItems="center">
        <TextField
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="small"
          label="Search by ingredient"
          placeholder="e.g. tomato"
          sx={{ flex: '1 1 auto', minWidth: 0 }}
        />
        <Button size="small" variant="contained" onClick={() => setSearchTerm(searchInput.trim())} sx={{ whiteSpace: 'nowrap' }}>Search</Button>
        <Button size="small" variant="outlined" onClick={() => { setSearchInput(''); setSearchTerm(''); }} sx={{ whiteSpace: 'nowrap' }}>Clear</Button>
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        {recipes.map((r) => (
          <Grid
            item
            key={r.id}
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              // spacing={2} on the Grid container produces 16px gaps.
              // For 3 columns there are two gaps (2 * 16 = 32px). Use calc() to subtract gutters.
              width: {
                xs: '100%',
                sm: 'calc((100% - 16px) / 2)',
                md: 'calc((100% - 32px) / 3)'
              },
              flex: {
                xs: '0 0 100%',
                sm: '0 0 calc((100% - 16px) / 2)',
                md: '0 0 calc((100% - 32px) / 3)'
              },
              minWidth: 0,
            }}
          >
            <Card
              onClick={() => navigate(pathnames.RecipeDetail.replace(':id', r.id))}
              sx={{ cursor: 'pointer', width: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}
            >
              {r.imageUrl && (
                <CardMedia component="img" height="160" image={r.imageUrl} alt={r.title || 'recipe image'} sx={{ width: '100%', height: 160, objectFit: 'cover' }} />
              )}
              <CardHeader title={r.title || 'Untitled'} subheader={r.sharedByDisplayName || ''} />
              <CardContent sx={{ flex: '1 1 auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {r.description || ''}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Ingredients</Typography>
                <List dense>
                  {(r.ingredients || []).slice(0,6).map((ing, idx) => (
                    <ListItem key={idx}>
                      <ListItemText primary={`${ing.quantity ?? ''} ${ing.unit ?? ''} ${ing.name ?? (typeof ing === 'string' ? ing : '')}`} secondary={ing.notes} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {!loading && recipes.length === 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography>{searchTerm ? 'No recipes match that ingredient.' : 'No recipes shared yet.'}</Typography>
        </Box>
      )}
      {!loading && hasMore && !searchTerm && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          {loadingMore ? <CircularProgress size={24} /> : <Button variant="contained" onClick={loadMore}>Load more</Button>}
        </Box>
      )}
    </Box>
  );
};

export default RecipesPage;
